import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { MODULES, STATUS_COLORS } from "@/config/modules";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { sortByDate } from "@/lib/sortUtils";
import ResourceForm from "@/components/ResourceForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportDialog";

const module = MODULES.payment_ledger;
const COMPANY_TABS = [["all", "All Companies"], ["BSPMT", "BSP Machines & Tools (BSPMT)"], ["BSPE", "BSP Enterprise (BSPE)"]];

export default function PaymentLedger() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "date", dir: "desc" });
  const toggleSort = () => setSort((s) => ({ field: "date", dir: s.dir === "asc" ? "desc" : "asc" }));
  const [deleteId, setDeleteId] = useState(null);
  const [party, setParty] = useState(null);
  const [company, setCompany] = useState("all");

  const load = useCallback(() => {
    if (!can("payment_ledger", "view")) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (company !== "all") params.issuer = company;
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.get("/resource/payment_ledger", { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, filters, company]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (!can("payment_ledger", "view"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <div className="h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4"><Icons.Lock className="h-6 w-6" /></div>
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have permission to view the Payment Ledger.</p>
      </div>
    );

  const totalIn = items.filter((i) => i.payment_type === "IN").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalOut = items.filter((i) => i.payment_type === "OUT").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const net = totalIn - totalOut;

  const remove = async () => {
    try { await api.delete(`/resource/payment_ledger/${deleteId}`); toast.success("Entry deleted"); setDeleteId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReceipt = async (mrId) => {
    const w = window.open("", "_blank");
    try { const r = await api.get(`/finance/MR/${mrId}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); if (w) w.location.href = u; else window.open(u, "_blank"); }
    catch (e) { w?.close(); toast.error(e.response?.status === 403 ? "You don't have permission to download receipts" : "Could not open Money Receipt"); }
  };
  const onSaved = (data, isNew) => {
    load();
    if (isNew && data?.money_receipt_id) {
      toast.success(`Money Receipt ${data.money_receipt_number} generated`, { action: { label: "View / Print", onClick: () => openReceipt(data.money_receipt_id) }, duration: 10000 });
    }
  };

  const downloadStatement = async () => {
    try {
      const res = await api.get("/ledger/statement", { params: { party }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statement_${party.replace(/[^a-z0-9]/gi, "_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate statement"); }
  };

  // Party ledger with running balance (chronological ascending by raw date)
  const partyEntries = party
    ? sortByDate(items.filter((i) => i.party_name === party), "date", "asc")
    : [];
  let bal = 0;
  const partyRows = partyEntries.map((e) => {
    const inn = e.payment_type === "IN" ? (parseFloat(e.amount) || 0) : 0;
    const out = e.payment_type === "OUT" ? (parseFloat(e.amount) || 0) : 0;
    bal += inn - out;
    return { ...e, inn, out, bal };
  });
  const pIn = partyRows.reduce((s, r) => s + r.inn, 0);
  const pOut = partyRows.reduce((s, r) => s + r.out, 0);

  const SUMS = [
    { label: "Total Payment In", value: totalIn, color: "#10B981", icon: "ArrowDownLeft" },
    { label: "Total Payment Out", value: totalOut, color: "#EF4444", icon: "ArrowUpRight" },
    { label: "Net Balance", value: net, color: "#4F46E5", icon: "Scale" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${module.accent}1a`, color: module.accent }}>
            <Icons.BookOpenCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Payment Ledger</h1>
            <p className="text-sm text-muted-foreground">{items.length} entr{items.length !== 1 ? "ies" : "y"} · party-wise Payment In / Out</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("payment_ledger", "export") && <ExportButton report="payment_ledger" title="Payment Ledger" testid="ledger-export-btn" filters={{ ...filters, issuer: company, q }} />}
        {can("payment_ledger", "create") && (
          <Button className="gap-1.5 active:scale-95 transition-transform" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-record-btn">
            <Icons.Plus className="h-4 w-4" />New Payment
          </Button>
        )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="ledger-company-tabs">
        {COMPANY_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setCompany(k)} data-testid={`ledger-company-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${company === k ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ledger-summary">
        {SUMS.map((s) => {
          const SIcon = Icons[s.icon];
          return (
            <Card key={s.label} className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}1a`, color: s.color }}><SIcon className="h-4 w-4" /></div>
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <div className="font-display text-2xl font-bold">{formatINR(s.value)}</div>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search party, reference, description, invoice…" className="pl-9" data-testid="module-search" />
        </div>
        {Object.entries(module.filters).map(([field, opts]) => (
          <Select key={field} value={filters[field] || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, [field]: v }))}>
            <SelectTrigger className="w-[160px]" data-testid={`filter-${field}`}><SelectValue placeholder={field.replace(/_/g, " ")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {field.replace(/_/g, " ")}</SelectItem>
              {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
      </div>

      {/* Table */}
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="overline text-muted-foreground">
                <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={toggleSort} data-testid="ledger-sort-date">
                  Date {sort.dir === "asc" ? <Icons.ArrowUp className="h-3 w-3" /> : <Icons.ArrowDown className="h-3 w-3" />}
                </button>
              </TableHead>
              {["Party", "Type", "Reference", "Description", "Mode", "Amount", "Status", ""].map((h, i) => <TableHead key={i} className="overline text-muted-foreground">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No payment entries yet.</TableCell></TableRow>
            )}
            {!loading && sortByDate(items, sort.field, sort.dir).map((row, i) => (
              <motion.tr key={row.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.2) }} className="border-b hover:bg-secondary/40">
                <TableCell>{row.date || "—"}</TableCell>
                <TableCell className="font-medium">
                  <button className="text-primary hover:underline text-left" onClick={() => setParty(row.party_name)} data-testid={`party-${row.id}`}>{row.party_name}</button>
                </TableCell>
                <TableCell className="whitespace-nowrap"><Badge variant="secondary" className={STATUS_COLORS[row.payment_type] || ""}>{row.payment_type}</Badge>{row.issuer && <Badge variant="outline" className="ml-1 text-[10px]" data-testid={`ledger-issuer-${row.id}`}>{row.issuer}</Badge>}</TableCell>
                <TableCell>
                  <div>{row.reference || "—"}</div>
                  {row.money_receipt_number && <button className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap" onClick={() => openReceipt(row.money_receipt_id)} data-testid={`mr-link-${row.id}`}><Icons.ReceiptText className="h-3 w-3" />{row.money_receipt_number}</button>}
                </TableCell>
                <TableCell className="max-w-[220px] truncate">{row.description || "—"}</TableCell>
                <TableCell>{row.payment_mode || "—"}</TableCell>
                <TableCell className="font-medium">{formatINR(row.amount)}</TableCell>
                <TableCell>{row.payment_status ? <Badge variant="secondary" className={STATUS_COLORS[row.payment_status] || ""}>{row.payment_status}</Badge> : "—"}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`row-menu-${row.id}`}><Icons.MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setParty(row.party_name)}><Icons.User className="h-4 w-4 mr-2" />Party Ledger</DropdownMenuItem>
                      {row.money_receipt_id && <DropdownMenuItem onClick={() => openReceipt(row.money_receipt_id)} data-testid={`mr-view-${row.id}`}><Icons.ReceiptText className="h-4 w-4 mr-2" />View / Print Money Receipt</DropdownMenuItem>}
                      {can("payment_ledger", "edit") && <DropdownMenuItem onClick={() => { setEditing(row); setFormOpen(true); }} data-testid={`edit-${row.id}`}><Icons.Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>}
                      {can("payment_ledger", "delete") && <DropdownMenuItem className="text-destructive" onClick={() => setDeleteId(row.id)} data-testid={`delete-${row.id}`}><Icons.Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} module={module} record={editing} onSaved={onSaved} />

      {/* Party profile ledger */}
      <Dialog open={!!party} onOpenChange={(o) => !o && setParty(null)}>
        <DialogContent className="max-w-3xl w-[95vw]" data-testid="party-ledger">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle className="font-display">{party}</DialogTitle>
                <DialogDescription>
                  In {formatINR(pIn)} · Out {formatINR(pOut)} · Balance {formatINR(pIn - pOut)}
                </DialogDescription>
              </div>
              <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={downloadStatement} data-testid="download-statement-btn">
                <Icons.FileDown className="h-4 w-4" />Download PDF
              </Button>
            </div>
          </DialogHeader>
          <div className="overflow-x-auto max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>{["Date", "Description", "Reference", "Payment In", "Payment Out", "Balance"].map((h) => <TableHead key={h} className="overline text-muted-foreground">{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {partyRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.date || "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{r.description || "—"}</TableCell>
                    <TableCell>{r.reference || "—"}</TableCell>
                    <TableCell className="text-emerald-600">{r.inn ? formatINR(r.inn) : "—"}</TableCell>
                    <TableCell className="text-red-600">{r.out ? formatINR(r.out) : "—"}</TableCell>
                    <TableCell className="font-medium">{formatINR(r.bal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payment entry?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
